import { useState } from "react";
import { toast } from "sonner";
import { HandCoins } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Loans go live after the rest of the shop, so their test data is cleared on its own. */
export const ResetLoansPanel = () => {
  const { can } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!can("users:manage")) return null;

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-loans", { confirm, password });
      toast.success(
        `${data.loans_deleted} test loan(s) cleared${
          data.files_purged ? ` and ${data.files_purged} attached file(s) removed` : ""
        }`
      );
      setConfirm("");
      setPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#C5A059]/60" data-testid="reset-loans-panel">
      <div className="border-b border-[#C5A059]/60 bg-[#C5A059]/10 px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
          <HandCoins className="h-4 w-4" /> Clear test loans only
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Loans are still in testing, so they go live after everything else. This deletes
          every pledge loan and its repayments, along with any files attached to them —
          and nothing else. Invoices, bullion sales, buying contracts, repairs, customers,
          account credit and the audit trail are all left alone. Use it the day loans go
          live. This cannot be undone.
        </p>
      </div>
      <div className="grid gap-4 p-5 sm:grid-cols-3 sm:items-end">
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Type RESET LOANS to confirm
          </Label>
          <Input
            className="h-10 rounded-none"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="RESET LOANS"
            data-testid="reset-loans-confirm-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Your admin password
          </Label>
          <Input
            type="password"
            className="h-10 rounded-none"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-loans-password-input"
          />
        </div>
        <Button
          onClick={run}
          disabled={busy || confirm !== "RESET LOANS" || !password}
          variant="outline"
          className="h-10 rounded-none border-[#C1272D] font-semibold text-[#C1272D] hover:bg-[#C1272D]/5"
          data-testid="reset-loans-button"
        >
          {busy ? "Clearing…" : "Clear test loans"}
        </Button>
      </div>
    </div>
  );
};
